import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Loader2, Sparkles, MapPin, Utensils, Fish, Drumstick, Sprout, Globe2,
  Wand2, Camera, CameraOff, HeartPulse, Lock,
} from "lucide-react";

const seasons = [
  { value: "spring", label: "Spring" },
  { value: "summer", label: "Summer" },
  { value: "autumn", label: "Autumn" },
  { value: "winter", label: "Winter" },
];

const courses = [
  { value: "amuse-bouche", label: "Nibble" },
  { value: "palate-cleanser", label: "Palate Cleanser" },
  { value: "starter", label: "Starter" },
  { value: "main", label: "Main" },
  { value: "dessert", label: "Pudding" },
];

const focuses = [
  { value: "any", label: "Surprise me", icon: Sparkles },
  { value: "seafood", label: "Seafood", icon: Fish },
  { value: "game", label: "Wild game", icon: Drumstick },
  { value: "poultry", label: "Poultry", icon: Drumstick },
  { value: "offal", label: "Offal", icon: HeartPulse },
  { value: "vegetarian", label: "Veg", icon: Sprout },
  { value: "vegan", label: "Vegan", icon: Sprout },
  { value: "world", label: "World cuisine", icon: Globe2 },
];

const suggestions = [
  "Something with mackerel",
  "A stew for a wet Tuesday",
  "Roast game bird, glass of red",
  "A curry from a Kerala street corner",
  "Beetroot doing something clever",
  "Late-summer tomatoes, showing off",
  "Late-night noodles",
];

const detectSeason = () => {
  const m = new Date().getMonth();
  if (m >= 2 && m <= 4) return "spring";
  if (m >= 5 && m <= 7) return "summer";
  if (m >= 8 && m <= 10) return "autumn";
  return "winter";
};

const Generator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dishIdea, setDishIdea] = useState("");
  const [region, setRegion] = useState("");
  const [season, setSeason] = useState(detectSeason());
  const [course, setCourse] = useState("main");
  const [focus, setFocus] = useState("any");
  const [dietary, setDietary] = useState("");
  const [generateImage, setGenerateImage] = useState(true);
  const [busy, setBusy] = useState(false);
  const [subInfo, setSubInfo] = useState(null); // {is_active, generations_today, generations_limit, generations_remaining}
  const autoSeason = detectSeason();

  useEffect(() => {
    if (!user) { setSubInfo(null); return; }
    api.get("/subscription/me")
      .then((r) => setSubInfo(r.data))
      .catch(() => setSubInfo(null));
  }, [user]);

  const refreshSub = () => {
    if (!user) return;
    api.get("/subscription/me").then((r) => setSubInfo(r.data)).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Sign in and grab a Kitchen Pass first.");
      navigate("/subscribe");
      return;
    }
    if (subInfo && !subInfo.is_active) {
      navigate("/subscribe");
      return;
    }
    if (subInfo && subInfo.generations_remaining <= 0) {
      toast.error(`Daily limit hit — you've generated ${subInfo.generations_limit} today.`);
      return;
    }
    if (!dishIdea.trim() || !region.trim()) {
      toast.error("Give us a rough idea and where you are, please");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/recipes/generate", {
        dish_idea: dishIdea.trim(),
        region: region.trim(),
        season,
        course,
        focus,
        dietary: dietary.trim() || null,
        generate_image: generateImage,
      });
      toast.success("Right, it's on the pass");
      refreshSub();
      navigate(`/recipe/${res.data.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "The kitchen's had a moment. Try again in a sec.";
      if (status === 401 || status === 402) {
        toast.error(msg);
        navigate("/subscribe");
      } else if (status === 429) {
        toast.error(msg);
        refreshSub();
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-24 bg-stone-950">
      <div className="max-w-3xl mx-auto px-6 sm:px-10">
        {/* Header */}
        <div className="mb-10 flex items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="magazine-number text-4xl">02</span>
              <div className="w-10 h-px bg-orange-600" />
              <span className="eyebrow">The Order Pad</span>
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-stone-100 leading-[0.95] sm:leading-[0.9] tracking-tight sm:tracking-tighter">
              So then — <span className="italic text-orange-600">what</span> are we cooking?
            </h1>
          </div>
        </div>

        {/* Paywall / quota strip */}
        {(!user || (subInfo && !subInfo.is_active)) && (
          <div
            data-testid="gen-paywall-banner"
            className="mb-10 border border-orange-800 bg-orange-950/20 p-6 flex flex-wrap items-center justify-between gap-4"
          >
            <div className="flex items-start gap-3 max-w-xl">
              <Lock className="w-4 h-4 text-orange-500 mt-1 shrink-0" />
              <div>
                <p className="font-serif-display text-xl text-stone-100 leading-tight">
                  {user ? "Kitchen Pass required" : "The Cinder & Sorrel kitchen is pass-only"}
                </p>
                <p className="mt-2 text-sm text-stone-400">
                  {user
                    ? "You're signed in — now grab a Kitchen Pass to send tickets to the pans. Five bespoke plates a day, Nano Banana plating, and every future feature on the house."
                    : "Sign in, grab a Kitchen Pass, and the whole Cinder & Sorrel line — Claude on the tickets, Nano Banana on the plating — starts firing to your order. Five plates a day, £7 a month or £77 a year."}
                </p>
              </div>
            </div>
            <Link
              to="/subscribe"
              data-testid="gen-paywall-cta"
              className="inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-6 py-3 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
            >
              See plans
            </Link>
          </div>
        )}

        {subInfo?.is_active && (
          <div
            data-testid="gen-quota-strip"
            className="mb-10 border border-lime-800/40 bg-lime-950/10 px-6 py-4 flex items-center justify-between gap-4 flex-wrap"
          >
            <div className="flex items-center gap-3">
              <Sparkles className="w-4 h-4 text-lime-500" />
              <span className="text-sm text-stone-200">
                <span className="font-serif-display italic text-lime-500 text-lg">
                  {subInfo.generations_remaining}
                </span>{" "}
                of {subInfo.generations_limit} recipes left today
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500">
              Kitchen Pass · resets midnight UTC
            </span>
          </div>
        )}

        <p className="text-stone-300 text-base md:text-lg leading-relaxed mb-12 max-w-xl">
          Scribble down a rough idea and where you are. We&apos;ll write the recipe —
          prep, method, plating, tasting notes, a pour to match, and a vegan version on the side.
        </p>

        <form onSubmit={submit} className="space-y-10 border-t border-stone-800 pt-10">
          {/* Dish Idea */}
          <div>
            <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
              <label className="eyebrow flex items-center gap-2">
                <Wand2 className="w-3.5 h-3.5 text-orange-600" strokeWidth={1.5} />
                What&apos;s the plan?
              </label>
              <span className="text-xs text-stone-500 font-mono-accent">
                Blank? Try one of these &darr;
              </span>
            </div>
            <input
              data-testid="gen-dish-idea"
              value={dishIdea}
              onChange={(e) => setDishIdea(e.target.value)}
              placeholder="Something with mackerel..."
              className="w-full bg-transparent border-0 border-b border-stone-700 focus:border-orange-600 outline-none py-4 font-serif-display text-2xl md:text-3xl text-stone-100 placeholder-stone-700 transition-colors"
              maxLength={200}
            />
            <div className="mt-4 flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`gen-suggestion-${s.slice(0, 10)}`}
                  onClick={() => setDishIdea(s)}
                  className="text-xs px-3 py-1.5 border border-stone-800 text-stone-400 hover:border-orange-600 hover:text-orange-500 transition-colors font-mono-accent"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Region */}
          <div>
            <label className="eyebrow flex items-center gap-2 mb-3">
              <MapPin className="w-3.5 h-3.5 text-orange-600" strokeWidth={1.5} />
              Where are you cooking?
            </label>
            <input
              data-testid="gen-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="West Highlands, Cornwall, Kerala, Brooklyn..."
              className="w-full bg-transparent border-0 border-b border-stone-700 focus:border-orange-600 outline-none py-4 font-serif-display text-2xl md:text-3xl text-stone-100 placeholder-stone-700 transition-colors"
              maxLength={100}
            />
          </div>

          {/* Focus */}
          <div>
            <label className="eyebrow flex items-center gap-2 mb-3">
              <Utensils className="w-3.5 h-3.5 text-orange-600" strokeWidth={1.5} />
              Any strong feelings?
            </label>
            <div className="flex flex-wrap gap-2">
              {focuses.map((f) => {
                const Icon = f.icon;
                const active = focus === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    data-testid={`gen-focus-${f.value}`}
                    onClick={() => setFocus(f.value)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
                      active
                        ? "border-orange-600 bg-orange-700/20 text-orange-500"
                        : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Season + Course (inline) */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-3 gap-2">
                <label className="eyebrow">Season</label>
                {season === autoSeason && (
                  <span className="text-[10px] text-stone-500 font-mono-accent uppercase tracking-widest">
                    Today
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {seasons.map((s) => {
                  const active = season === s.value;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      data-testid={`gen-season-${s.value}`}
                      onClick={() => setSeason(s.value)}
                      className={`py-2.5 text-[11px] uppercase tracking-widest border transition-colors ${
                        active
                          ? "border-orange-600 bg-orange-700/20 text-orange-500"
                          : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
                      }`}
                    >
                      {s.label.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-3">Course</label>
              <div className="flex flex-wrap gap-1.5">
                {courses.map((c) => {
                  const active = course === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      data-testid={`gen-course-${c.value}`}
                      onClick={() => setCourse(c.value)}
                      className={`px-3 py-2.5 text-[11px] uppercase tracking-widest border transition-colors ${
                        active
                          ? "border-orange-600 bg-orange-700/20 text-orange-500"
                          : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Dietary - collapsed feel */}
          <div>
            <label className="eyebrow block mb-3">Anything to avoid? <span className="text-stone-600 normal-case tracking-normal">(optional)</span></label>
            <input
              data-testid="gen-dietary"
              value={dietary}
              onChange={(e) => setDietary(e.target.value)}
              placeholder="gluten-free, no shellfish, coriander is the devil..."
              className="w-full bg-stone-900/60 border border-stone-800 focus:border-orange-600 outline-none px-4 py-3 text-stone-200 placeholder-stone-600 text-sm transition-colors"
              maxLength={200}
            />
          </div>

          {/* Submit row with inline image toggle */}
          <div className="pt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 border-t border-stone-800">
            <button
              type="submit"
              disabled={busy}
              data-testid="gen-submit"
              className="group inline-flex items-center justify-center gap-3 bg-orange-700 hover:bg-orange-800 disabled:opacity-70 text-stone-50 px-8 py-4 uppercase tracking-[0.28em] text-xs font-medium transition-colors flex-1"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
              {busy ? "Sharpening the knives..." : "Send it to the kitchen"}
            </button>
            <button
              type="button"
              data-testid="gen-image-toggle"
              onClick={() => setGenerateImage((v) => !v)}
              title={generateImage ? "Photograph on — turns up shortly after the recipe" : "Photograph off"}
              className={`inline-flex items-center justify-center gap-2 px-5 py-4 text-[11px] uppercase tracking-[0.24em] border transition-colors ${
                generateImage
                  ? "border-orange-600 text-orange-500 bg-orange-700/10"
                  : "border-stone-800 text-stone-500 hover:border-stone-600"
              }`}
            >
              {generateImage ? <Camera className="w-4 h-4" strokeWidth={1.5} /> : <CameraOff className="w-4 h-4" strokeWidth={1.5} />}
              {generateImage ? "Photograph on" : "No photo"}
            </button>
          </div>

          {busy && (
            <p className="text-sm text-stone-500 italic font-serif-display text-center">
              Give us a moment — good things take about the length of a decent glass of wine.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Generator;
